'use client';

import { useTranslations } from 'next-intl';
import { Battery, Charger, InfoIcon, Return, ShippingBox } from 'assets/icons';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { useSearchParams } from 'next/navigation';
import { forwardRef, RefObject, useContext, useRef } from 'react';
import {
  AirRingColors,
  getAvailableAirRingColors,
  getEngravingKey,
  MiscRingCheckoutTypes,
  productMap_air_ring,
  RingPriceData,
  RingSizes,
} from 'content/products/ring';
import { RingAirCarouselData } from 'content/products/ring/productCarouselImages';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { useWindowSize } from 'hooks/dom/useWindowSize';
// import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  Container,
  CustomImage as Image,
  useModal,
  PHONE_MAX_WIDTH_IN_PX,
  getShippingAndTaxDisclaimerMap,
  DotSpinner,
} from '..';
import { useCartStore } from 'components/shared/Cart';
import {
  LeftPanel,
  PanelContainer,
  RightPanel,
} from './ProductSelectionSection';
import { RingAirProductCarousel } from './ProductSelectionSection/RingAirProductCarousel';
import { RingSelectionContext } from './RingSelectionContext';
import { H2 } from './styleConstants';
import styles from './ProductSelectionSectionAlternate.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

import { GlobalUIContext, useGlobalUI } from 'contexts/GlobalUIContext';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';

const RingHeader = ({ className }: { className?: string }) => {
  const { liteUI } = useGlobalUI();
  const tCommon = useTranslations('common');

  return (
    <div
      className={cx(styles.ringHeader, className, liteUI ? 'sticky' : '')}
    >
      <Container>
        <div className="content">
          <h4>{tCommon('subheading.ultrahumanRingAir')}</h4>
        </div>
      </Container>
    </div>
  );
};

// Main Export Here 👇🏻
export const ProductSelectionSectionAlternate = ({
  className,
  misc,
}: {
  className?: string;
  misc?: MiscRingCheckoutTypes;
}) => {
    const { width } = useWindowSize();
    const globalUI = useContext(GlobalUIContext);
    const RingSelectionClient = useContext(RingSelectionContext);
    const { liteUI } = useGlobalUI();
    const { notServicable } = RingSelectionClient;

    return (
      <section
        className={cx(styles.root, 'u-grid', className, liteUI ? 'pt-0' : '')}
        data-banner={isMarketingBannerActive() ? 'true' : 'false'}
      >
        <div className="content">
          <RingHeader />
          <PanelContainer
            style={{ filter: notServicable ? 'grayscale(1)' : undefined }}
          >
            {(width as number) > PHONE_MAX_WIDTH_IN_PX && (
              <LeftPanel>
                <StickyPanel $headerVisible={globalUI?.headerVisible ?? true}>
                  <div>
                    <RingAirProductCarousel
                      selectedColor={RingSelectionClient.color as AirRingColors}
                      items={
                        (RingSelectionClient.color
                          ? RingAirCarouselData[RingSelectionClient.color]
                          : RingAirCarouselData[AirRingColors.ASTER_BLACK]) ??
                        []
                      }
                      sticky={true}
                    />
                  </div>
                </StickyPanel>
              </LeftPanel>
            )}
            <RightPanel>
              <BuySection misc={misc} />
            </RightPanel>
          </PanelContainer>
        </div>
      </section>
    );
};

const StickyPanel = ({
  $headerVisible,
  children,
  ...props
}: {
  $headerVisible: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={styles.stickyPanel}
    data-header-visible={$headerVisible ? 'true' : 'false'}
    {...props}
  >
    {children}
  </div>
);

const SizeInfoModal = ({ className }: { className?: string }) => {
  const tCommon = useTranslations('common');
  return (
    <div className={cx(styles.sizeInfoModal, className)}>
      <h2>{tCommon('heading.ringSizingInstructions')}</h2>
      <p>
        {tCommon('productSelectionSectionAlternate.text.findYourBest')}
        <br />
        <br />
        {tCommon('productSelectionSectionAlternate.text.knowYourUltrahuman')}
      </p>
    </div>
  );
};

const Section = forwardRef<
  HTMLDivElement,
  {
    className?: string;
    title?: React.ReactNode | string;
    openInfoModal?: () => void;
    children: React.ReactNode;
    childClass: string;
    sectionClass?: string;
    show: boolean;
    disableScrollToScreen?: boolean;
  }
>(
  (
    {
      className,
      title,
      openInfoModal,
      children,
      childClass,
      sectionClass,
      show,
    },
    ref,
  ) => {
      return (
        <div
          ref={ref}
          className={cx(
            styles.section,
            className,
            show ? 'show' : '',
            sectionClass,
          )}
        >
          {title ? (
            <div className="section-row-title">
              <h5>{title}</h5>
              {openInfoModal ? (
                <button onClick={openInfoModal} style={{ padding: '0 8px' }}>
                  {/* Sizing guide <CaretUp style={{ transform: 'rotate(90deg)', height: '0.8rem' }} /> */}
                  <InfoIcon
                    style={{
                      width: '1.6rem',
                      height: 'auto',
                      marginTop: '4px',
                    }}
                    fill="#000"
                  />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className={`section-row-content ${childClass}`}>{children}</div>
        </div>
      );
    },
);

Section.displayName = 'Section';

const BuySection = ({
  className,
  misc,
}: {
  className?: string;
  misc?: MiscRingCheckoutTypes;
}) => {
    const searchParams = useSearchParams();
    const RingSelectionClient = useContext(RingSelectionContext);
    const store = useCartStore((s) => s.store);
    const { width } = useWindowSize();
    const ModalClient = useModal();
    const _t = useTranslations('ring');
    const tCart = useTranslations('cart');
    const tCommon = useTranslations('common');
    const {
      color,
      setColor,

      size,
      setSize,

      engraving,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setEngraving,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      engravingMessage,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setEngravingMessage,

      checkout,
      isLoading,

      price,
      currencySymbol,
      ultraHumanPrice,
      chargerPrice,
      shippingEstimate,
      affiliateDetails,
      campaignActive,
      affiliateDiscount,
      notServicable,
      notServicableMessage,
    } = RingSelectionClient;

    const showSizeRef = useRef(null);
    const cartSummaryRef = useRef(null);
    const formatPriceWithComma = useFormatPrice();

    const RingAirColorNameImages = {
      [AirRingColors.RAW_TITANIUM]: {
        image: 'web_v2/ring-buy/color-cta/rt-2.png/w_400',
        name: 'Raw Titanium',
        tag: null,
      },
      [AirRingColors.ASTER_BLACK]: {
        image: 'web_v2/ring-buy/color-cta/ab-2.png/w_400',
        name: 'Aster Black',
        tag: null,
      },
      [AirRingColors.MATTE_GREY]: {
        image: 'web_v2/ring-buy/color-cta/mb-2.png/w_400',
        name: 'Matte Grey',
        tag: null,
      },
      [AirRingColors.BIONIC_GOLD]: {
        image: 'web_v2/ring-buy/color-cta/bg-2.png/w_400',
        name: 'Bionic Gold',
        tag: null,
      },
      [AirRingColors.SPACE_SILVER]: {
        image: 'web_v2/ring-buy/color-cta/ss-2.png/w_400',
        name: 'Space Silver',
        tag: null,
      },
      [AirRingColors.ROSE_GOLD]: {
        image: 'web_v2/ring-buy/color-cta/rg-4.png/w_400',
        name: 'Brushed Rose Gold',
        tag: null,
      },
    };

    const scrollTo = (ref: RefObject<HTMLDivElement | null>) => {
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 200);
    };

    const openSizeInfoModal = () => {
      ModalClient.set(<SizeInfoModal />);
      ModalClient.show();
    };

    const handleCheckout = () => {
      if (isLoading) return;
      checkout();
      ModalClient.hide();
    };

    const effectiveStoreForAffiliate = getEffectiveStoreForDiscount(store);

    const showAffiliate =
      !campaignActive &&
      affiliateDetails &&
      affiliateDetails.discount_configs[
        effectiveStoreForAffiliate as ShopifyStore
      ]?.RING?.code &&
      affiliateDiscount;

    if (!store || !color) return null;

    return (
      <div className={cx(styles.buySection, className)}>
        <div className="buy-section title-info-content">
          <H2>{tCommon('productSelectionSectionAlternate.h2.ringAir')} </H2>
          <p className={`price`}>
            <span
              className={
                misc === MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT
                  ? 'strike-through'
                  : ''
              }
            >
              {
                CurrencySymbol[
                  RingSelectionClient.defaultCurrency.toLowerCase() as Currency
                ]
              }
              {formatPriceWithComma(
                RingPriceData[store as ShopifyStore][color as AirRingColors]
                  .striked_price,
              )}
            </span>

            {misc === MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT ? (
              <span>
                {tCommon('productSelectionSectionAlternate.text.free')}
                {/* <span className="tax-disclaimer">
                  {' '}
                  excl. shipping and taxes
                </span> */}
              </span>
            ) : null}

            {false && (
              <span className="early-bird-chip">
                {tCommon('productSelectionSectionAlternate.text.earlyBirdPrice')}
              </span>
            )}
          </p>

          {misc === MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT && (
            <p className="shipping-estimate partner">
              <Image
                src="web_v2/logos/partners/better-help.svg"
                height="20"
                width="120"
                alt={tCommon(
                  'productSelectionSectionAlternate.image.alt.betterhelp',
                )}
                style={{
                  display: 'inline',
                  marginRight: '8px',
                  transform: 'translateY(4px)',
                }}
              />
              {searchParams?.get('discount') && (
                <span>
                  &apos;{searchParams?.get('discount')}
                  {tCommon(
                    'productSelectionSectionAlternate.text.discountCodeApplied',
                  )}
                </span>
              )}
            </p>
          )}

          {/* <p className="shipping-estimate">
            {defaultUnSelectedShipplingEstimate}
          </p> */}

          <p className="description">
            {tCommon(
              'productSelectionSectionAlternate.text.comfortableSleekSmart',
            )}
          </p>
        </div>
        {(width as number) < PHONE_MAX_WIDTH_IN_PX && (
          <div
            className="carousel-section"
            style={{ filter: notServicable ? 'grayscale(1)' : undefined }}
          >
            <RingAirProductCarousel
              selectedColor={RingSelectionClient.color as AirRingColors}
              items={
                (RingSelectionClient.color
                  ? RingAirCarouselData[RingSelectionClient.color]
                  : RingAirCarouselData[AirRingColors.ASTER_BLACK]) ?? []
              }
              sticky={false}
            />
          </div>
        )}

        {/* Color */}
        <Section
          title={tCommon('section.title.selectYourRing')}
          childClass="color-section"
          show={true}
          disableScrollToScreen={true}
          sectionClass="color-section"
        >
          <>
            <div className="colors">
              {getAvailableAirRingColors(store).map((element, index) => {
                return (
                  <button
                    style={{
                      transitionDelay: `${index * 0.05}s`,
                      transitionProperty: 'opacity',
                    }}
                    key={index}
                    className={`${element == color ? 'active' : ''}`}
                    onClick={() => {
                      setColor(element as AirRingColors);
                      scrollTo(showSizeRef);
                    }}
                  >
                    {RingAirColorNameImages[element].tag && (
                      <b className="tag">
                        {RingAirColorNameImages[element].tag}
                      </b>
                    )}
                    <Image
                      width={72}
                      height={72}
                      src={RingAirColorNameImages[element].image}
                      alt={element}
                    />
                    {RingAirColorNameImages[element].name}
                  </button>
                );
              })}
            </div>
          </>
        </Section>

        {/* Size */}
        <Section
          title={tCommon('section.title.selectYourRing2')}
          childClass="sizing-section"
          openInfoModal={openSizeInfoModal}
          show={!!color}
          disableScrollToScreen={true}
          sectionClass="size-section"
          ref={showSizeRef}
        >
          <>
            {color &&
            size &&
            productMap_air_ring[color]?.[size]?.[getEngravingKey(engraving)]
              ?.SKU ? (
              <div className="size-shipping">
                <ShippingBox />
                <p>{shippingEstimate}</p>
              </div>
            ) : null}
            <div className="sizes">
              {Object.values(RingSizes)
                .filter(
                  (value) =>
                    typeof value === 'number' && value !== RingSizes.OPEN,
                )
                .map((element, index) => {
                  return (
                    <button
                      style={{
                        transitionDelay: `${index * 0.05}s`,
                        transitionProperty: 'opacity',
                      }}
                      key={index}
                      className={`${size == element ? 'active' : ''}`}
                      disabled={
                        color &&
                        productMap_air_ring[color as AirRingColors]?.[
                          element as RingSizes
                        ]?.[getEngravingKey(engraving)]?.variantId[
                          store as ShopifyStore
                        ]
                          ? false
                          : true
                      }
                      onClick={() => {
                        setSize(element as RingSizes);
                        if (width > 786) scrollTo(cartSummaryRef);
                      }}
                    >
                      {element}
                    </button>
                  );
                })}
            </div>

            <div className="divider">
              {tCommon('productSelectionSectionAlternate.content')}
            </div>

            <button
              className={`sizing-kit ${size == RingSizes.OPEN ? 'active' : ''}`}
              onClick={() => {
                setSize(RingSizes.OPEN);
                if (width > 786) scrollTo(cartSummaryRef);
              }}
            >
              <div className="button-content">
                <div className="img">
                  <Image
                    src={'web/ring-buy/sizing-kit.webp'}
                    alt={tCommon(
                      'productSelectionSectionAlternate.image.alt.sizingKit',
                    )}
                    width="64"
                    height="64"
                    loading="lazy"
                    className="sizing-kit-img"
                  />
                </div>
                <div className="content">
                  <h5>
                    {tCommon(
                      'productSelectionSectionAlternate.label.optRingAir',
                    )}
                  </h5>
                  <p>
                    {tCommon('productSelectionSectionAlternate.text.weLlShip')}
                  </p>
                </div>
              </div>
              {/*CONFIG: <div className="sizing-kit-delivery">
                <ShippingBox />
                <p>Dispatched under 24 hours</p>
              </div> */}
            </button>
          </>
        </Section>

        {/* Not Servicable Message */}
        {notServicable ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: '1.4rem',
              lineHeight: '1.5',
              marginTop: '16px',
            }}
          >
            <InfoIcon
              style={{ width: '24px', height: '24px', flexShrink: 0 }}
            />
            {notServicableMessage || 'Currently Unavailable'}
          </div>
        ) : null}

        {/* Overview */}
        {size && !notServicable ? (
          <Section childClass="cart-summary" show={!!size} ref={cartSummaryRef}>
            <div className="card" id="order-summary-card">
              <h4>
                {tCommon(
                  'productSelectionSectionAlternate.subheading.almostThereHere',
                )}
              </h4>
              <div className="content">
                <h5>
                  {tCommon(
                    'productSelectionSectionAlternate.label.ultrahumanRingAir',
                  )}{' '}
                  {engraving
                    ? tCommon(
                        'productSelectionSectionAlternate.label.engraving',
                      )
                    : null}
                </h5>
                {size ? (
                  <p className="size">
                    {color ? `${RingAirColorNameImages[color].name}` : ''},{' '}
                    {size === RingSizes.OPEN
                      ? tCommon(
                          'productSelectionSectionAlternate.text.sizingKitIncluded',
                        )
                      : ` Size ${size}`}
                  </p>
                ) : null}

                {showAffiliate ? (
                  <div className="price-breakup">
                    <p>
                      {tCommon(
                        'productSelectionSectionAlternate.text.affiliateDiscount',
                      )}
                    </p>
                    <p>
                      -{currencySymbol}
                      {affiliateDiscount}
                    </p>
                  </div>
                ) : null}
              </div>

              {size ? (
                <div className="total">
                  <div className="total-bar">
                    <h5>
                      {tCommon(
                        'productSelectionSectionAlternate.label.total',
                      )}
                    </h5>{' '}
                    {!misc ? (
                      <p>
                        {currencySymbol}
                        {formatPriceWithComma(
                          price +
                            ultraHumanPrice +
                            chargerPrice -
                            affiliateDiscount,
                        )}
                      </p>
                    ) : null}
                    {misc === MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT ? (
                      <p>
                        {tCommon(
                          'productSelectionSectionAlternate.text.free',
                        )}
                      </p>
                    ) : null}
                  </div>

                  <p className="taxes">
                    {getShippingAndTaxDisclaimerMap(tCart)[store as ShopifyStore]}
                  </p>

                  <button
                    className="checkout"
                    disabled={!size || isLoading || notServicable}
                    onClick={handleCheckout}
                    aria-busy={isLoading}
                  >
                    {isLoading ? (
                      <DotSpinner color="#000000" />
                    ) : (
                      <>
                        {misc === MiscRingCheckoutTypes.BETTER_HELP_CHECKOUT
                          ? tCommon(
                              'productSelectionSectionAlternate.button.continueCheckout',
                            )
                          : ''}
                        {!misc
                          ? tCommon(
                              'productSelectionSectionAlternate.button.addCart',
                            )
                          : null}
                      </>
                    )}
                  </button>
                </div>
              ) : null}
              <div className="benefits">
                <div className="charger benefit">
                  <Charger />
                  <div>
                    <p>
                      {tCommon(
                        'productSelectionSectionAlternate.text.standardCharger',
                      )}{' '}
                    </p>
                    <p>
                      {tCommon(
                        'productSelectionSectionAlternate.text.included',
                      )}
                    </p>
                  </div>
                </div>
                <div className="battery benefit">
                  <Battery />
                  <p>
                    {tCommon(
                      'productSelectionSectionAlternate.text.daysBatteryLife',
                    )}
                  </p>
                </div>
                <div className="return benefit">
                  <Return />
                  <p>
                    {tCommon(
                      'productSelectionSectionAlternate.text.30DayReturn',
                    )}
                  </p>
                </div>
              </div>
            </div>
          </Section>
        ) : null}
      </div>
    );
};
